import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PushPin, PencilSimple, Check, X as XIcon, Warning } from "@phosphor-icons/react";

// Two modes:
// - readOnly (default): shows the pinned notes as a warning banner on JobDetail — no edit UI.
// - editable: on PropertyDetail (admin only), shows a full editor.
export default function PropertyNotesCard({ address, readOnly = true, initialNotes, initialMeta }) {
  const [notes, setNotes] = useState(initialNotes ?? null);
  const [meta, setMeta] = useState(initialMeta || null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initialNotes !== undefined) {
      setNotes(initialNotes || "");
      setDraft(initialNotes || "");
      return;
    }
    if (!address || address.length < 3) return;
    api.get("/property-config", { params: { address }})
      .then(r => {
        setNotes(r.data.notes || "");
        setDraft(r.data.notes || "");
        setMeta({ at: r.data.notes_updated_at, by: r.data.notes_updated_by_name });
      })
      .catch(() => setNotes(""));
  }, [address, initialNotes]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/property-notes", { address, notes: draft });
      setNotes(r.data.notes);
      setMeta({ at: r.data.notes_updated_at, by: r.data.notes_updated_by_name });
      setEditing(false);
      toast.success("Property notes saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  if (notes === null) return null;

  // Read-only pinned banner (JobDetail) — hidden entirely when empty
  if (readOnly) {
    if (!notes) return null;
    return (
      <div className="rugged-card p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="property-notes-banner">
        <div className="flex items-center gap-2">
          <PushPin weight="fill" size={18} className="text-amber-700"/>
          <h3 className="font-display font-black text-sm uppercase tracking-wider text-amber-900">Property Notes</h3>
          {meta?.by && <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider ml-auto">by {meta.by}</span>}
        </div>
        <p className="mt-1.5 text-sm text-amber-900 whitespace-pre-wrap font-medium">{notes}</p>
      </div>
    );
  }

  // Editable card (PropertyDetail)
  return (
    <div className="rugged-card p-4 bg-white border-l-4 border-amber-500" data-testid="property-notes-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <PushPin weight="fill" size={18} className="text-amber-700"/>
          <h3 className="font-display font-black text-sm uppercase tracking-wider">Pinned Property Notes</h3>
        </div>
        {!editing && (
          <button onClick={() => { setDraft(notes || ""); setEditing(true); }}
            data-testid="property-notes-edit-btn"
            className="h-8 px-2 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-wider inline-flex items-center gap-1 hover:bg-slate-50">
            <PencilSimple weight="bold" size={12}/> {notes ? "Edit" : "Add"}
          </button>
        )}
      </div>

      {editing ? (
        <>
          <textarea value={draft} onChange={e => setDraft(e.target.value)}
            data-testid="property-notes-input"
            placeholder="Gate code, quirks, tenant temperament, parking, dogs on site, best time to visit, HOA rules — anything the crew should know before arriving."
            className="mt-2 w-full min-h-[110px] border-2 border-[#0f172a] p-2 text-sm"/>
          <div className="mt-2 text-[10px] text-slate-500 uppercase font-black tracking-wider">
            Visible to every worker on every job at this address
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={save} disabled={busy}
              data-testid="property-notes-save-btn"
              className="tactile-btn h-9 px-3 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 disabled:opacity-50">
              <Check weight="bold" size={14}/> {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setDraft(notes || ""); setEditing(false); }}
              data-testid="property-notes-cancel-btn"
              className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-1">
              <XIcon weight="bold" size={14}/> Cancel
            </button>
          </div>
        </>
      ) : notes ? (
        <>
          <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap font-medium">{notes}</p>
          {meta?.by && (
            <div className="mt-2 text-[10px] text-slate-500 font-bold uppercase tracking-wider">
              Last edited by {meta.by}
              {meta.at && ` · ${new Date(meta.at).toLocaleString()}`}
            </div>
          )}
        </>
      ) : (
        <div className="mt-2 flex items-center gap-2 text-slate-500 text-sm">
          <Warning weight="bold" size={16}/>
          <span className="italic">No pinned notes yet. Add gate codes, tenant quirks, or anything the crew needs before walking up.</span>
        </div>
      )}
    </div>
  );
}
